import {
  Configuration,
  type ContactResponse,
  createContactResponse,
} from "@/types/campaign";
import {
  createNamedErrorType,
  LassoErrorCode,
  lassoExceptionSchema,
} from "@/types/lasso-exception";
import { api } from "@/utils/api";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { HTTPError } from "ky";

type RoleResponse = {
  level: string;
  group: string;
  totalCount: number;
  queryCount: number;
};

type TitlesQueryParams = {
  level?: string[];
  group?: string[];
  title?: string;
  titleOperator?: "Equal" | "Contains";
};

export type TitleResponse = {
  title: string | null;
  totalCount: number;
  queryCount: number;
};

export type HistoryContact = {
  id: string;
  uniqueId: string | null;
  titles: string[];
  group: string | null;
  type: string;
  emails: number;
  phoneNumbers: number;
  links: number;
  nameLength: number;
  possiblePhoneNumbers: number;
  paths: string[];
  roles?: Array<{
    level: string;
    groups: string[];
    title: string;
  }>;
  hasLeadershipRole?: boolean;
};

export type HistoryResponse = Array<{
  contacts: HistoryContact[];
  phoneData: unknown[];
  emailData: unknown[];
  linkData: unknown[];
  emails: number;
  phoneNumbers: number;
  links: number;
  url: string;
  website: string;
  timestamp: string;
  verified: boolean;
}>;

export type GroupResponse = {
  group: string | null;
  totalCount: number;
  queryCount: number;
};

export const PaymentRequiredError = createNamedErrorType({
  paymentRequired: () =>
    "Du har ikke nok credits til at købe dette telefonnummer.",
});

export const contactsApi = {
  get: async (
    lassoId: string,
    contactId: string,
    purchaseContact = false,
    signal?: AbortSignal,
    refresh = false,
  ) => {
    const queryParams = new URLSearchParams();
    queryParams.append("purchase", purchaseContact ? "true" : "false");
    if (refresh) queryParams.append("refresh", "true");
    try {
      return await api
        .get<ContactResponse | null>(
          `apps/sales/leads/${lassoId}/contacts/${contactId}?${queryParams.toString()}`,
          { signal },
        )
        .json();
    } catch (error) {
      if (error instanceof HTTPError) {
        const json = await error.response.json().catch(() => null);
        const { data: lassoException } = lassoExceptionSchema.safeParse(json);

        if (lassoException) {
          switch (lassoException.errorCode) {
            case LassoErrorCode.LimitReachedError:
              throw new PaymentRequiredError("paymentRequired");

            default:
              throw new Error(
                `Unhandled error code: ${lassoException.errorCode}, message: ${lassoException.errorMessage}`,
              );
          }
        }
      }
      throw error;
    }
  },
  getHistory: async (lassoId: string) => {
    return await api
      .get<HistoryResponse>(`data/websitecrawler/${lassoId}/history`)
      .json()
      .catch(() => [] as HistoryResponse);
  },

  getTitles: async (params: TitlesQueryParams = {}, signal?: AbortSignal) => {
    const { level, group, title, titleOperator = "Contains" } = params;

    const queryParams = new URLSearchParams();

    if (level?.length) queryParams.append("level", level.join("|"));
    if (group?.length) queryParams.append("group", group.join("|"));
    if (title) queryParams.append("title", title);
    queryParams.append("titleOperator", titleOperator);

    return await api
      .post(
        `apps/sales/configuration/contacts/titles?${queryParams.toString()}`,
        { signal },
      )
      .json<TitleResponse[]>();
  },
};

export const createContactRolesQueryOptions = (
  configurations: Configuration[],
) =>
  queryOptions({
    queryKey: ["contact-roles", configurations],
    queryFn: () =>
      api
        .post("apps/sales/configuration/contacts/counts", {
          json: configurations?.map((c) => c.query),
        })
        .json<RoleResponse[]>(),
    initialData: [],
  });

export const useContactRolesQuery = (configurations: Configuration[]) => {
  return useQuery(createContactRolesQueryOptions(configurations));
};

export const createContactTitlesQueryOptions = (
  params: TitlesQueryParams = {},
) =>
  queryOptions({
    queryKey: ["contact-titles", params],
    queryFn: () => contactsApi.getTitles(params),
  });

export const useContactTitlesQuery = (params: TitlesQueryParams = {}) => {
  return useQuery(createContactTitlesQueryOptions(params));
};

const contactNotFoundError = new Error("Contact not found");

export const createLeadContactQueryOptions = (
  leadId: string,
  contactId?: string,
  purchaseContact = false,
  options?: {
    enabled?: boolean;
    placeholderData?: boolean;
  },
) =>
  queryOptions({
    queryKey: [
      "lead-contact",
      leadId,
      contactId,
      purchaseContact ? "with-contact-info" : "without-contact-info",
    ],
    queryFn: async ({ signal }) => {
      if (!contactId) return null;
      const response = await contactsApi.get(
        leadId,
        contactId,
        purchaseContact,
        signal,
      );
      if (response === null) {
        throw contactNotFoundError;
      }
      return response;
    },
    retry: (failureCount, error) => {
      if (error === contactNotFoundError) {
        return false;
      }
      if (error instanceof Error && error.name === "paymentRequired") {
        return false;
      }

      return failureCount < 4;
    },
    ...(options?.placeholderData !== false && {
      placeholderData: createContactResponse({
        name: "Indlæser...",
        emails: ["Indlæser..."],
        phoneNumbers: ["Indlæser..."],
      }),
    }),
    retryDelay: 2000,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    enabled: options?.enabled,
  });

export const createHistoryContactsQueryOptions = (lassoId: string) =>
  queryOptions({
    queryKey: ["history-contacts", lassoId],
    queryFn: async () => {
      return await contactsApi.getHistory(lassoId);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

// TanStack Query hook for fetching a single contact
export const useHistoryContactsQuery = (lassoId: string) => {
  return useQuery(createHistoryContactsQueryOptions(lassoId));
};
